/**
 * Shared iframe building utilities for design previews.
 * Used by both DesignPanel (full preview) and SectionParallelView (AI Overview).
 */

/** A published bundle must be present before a generating page can run in an iframe. */
export function hasCompiledDesignPreview(html?: string): boolean {
  return Boolean(html && html.trim().length > 100);
}

/** Normalize a preview navigation target into a comparable page slug. */
export function normalizeDesignNavigationTarget(rawTarget: string): string | null {
  const trimmed = rawTarget.trim();

  if (!trimmed || trimmed.startsWith('#') || /^(javascript|mailto|tel):/i.test(trimmed)) return null;

  let pathname = trimmed;

  try {
    const url = new URL(trimmed, window.location.origin);

    if (/^https?:/i.test(url.protocol) && url.origin !== window.location.origin) return null;
    pathname = url.pathname || trimmed;
  } catch {
    pathname = trimmed;
  }

  const clean = pathname
    .split(/[?#]/, 1)[0]
    .replace(/^\/+/, '')
    .replace(/\.html?$/i, '');

  if (!clean) return null;

  const segments = clean.split('/').filter(Boolean);
  const target = segments[segments.length - 1] || clean;

  return decodeURIComponent(target);
}

/** Injected into every iframe to intercept internal preview navigation clicks. */
export const IFRAME_LINK_INTERCEPT_SCRIPT = [
  '<script>',
  'function __isPreviewNavigableHref(href) {',
  '  if (!href) return false;',
  '  var value = href.trim();',
  '  if (!value || value[0] === "#" || /^(javascript|mailto|tel):/i.test(value)) return false;',
  '  try {',
  '    var url = new URL(value, window.location.origin);',
  '    return url.origin === window.location.origin;',
  '  } catch (_err) {',
  '    return true;',
  '  }',
  '}',
  // ── 1. Intercept <a> clicks ──
  'document.addEventListener("click", function(e) {',
  '  var a = e.target; while (a && a.tagName !== "A") a = a.parentElement;',
  '  if (!a || !a.hasAttribute("href")) return;',
  '  var href = a.getAttribute("href") || "";',
  '  if (a.target === "_blank" || a.hasAttribute("download")) return;',
  '  if (/^(javascript|mailto|tel):/i.test(href)) return;',
  '  e.preventDefault(); // Stop all other links (including href="" and href="#") from navigating!',
  '  if (href && href !== "#" && __isPreviewNavigableHref(href)) {',
  '    window.parent.postMessage({ type: "design-navigate", path: href, pathname: a.pathname || "" }, "*");',
  '  }',
  '}, true);',
  // ── 2. Intercept form submissions ──
  'document.addEventListener("submit", function(e) {',
  '  if (e.defaultPrevented) return; // Let application submit handlers own their forms.',
  '  var form = e.target;',
  '  if (form && form.tagName === "FORM") {',
  '    var action = form.getAttribute("action") || "";',
  '    if (/^(javascript|mailto|tel):/i.test(action)) return;',
  '    e.preventDefault(); // Stop forms from submitting/navigating',
  '    if (action && action !== "#" && __isPreviewNavigableHref(action)) {',
  '      window.parent.postMessage({ type: "design-navigate", path: action, pathname: "" }, "*");',
  '    }',
  '  }',
  '}, false);',
  // ── 3. Override programmatic navigation ──
  'try {',
  '  var _origAssign = window.location.assign.bind(window.location);',
  '  var _origReplace = window.location.replace.bind(window.location);',
  '  window.location.assign = function(url) {',
  '    if (__isPreviewNavigableHref(url)) {',
  '      window.parent.postMessage({ type: "design-navigate", path: url, pathname: "" }, "*");',
  '    }',
  '  };',
  '  window.location.replace = function(url) {',
  '    if (__isPreviewNavigableHref(url)) {',
  '      window.parent.postMessage({ type: "design-navigate", path: url, pathname: "" }, "*");',
  '    }',
  '  };',
  '} catch(_e) {}',
  '</script>',
].join('\n');

const PREVIEW_SURFACE_SYNC_SCRIPT = [
  '<script>',
  '(function () {',
  '  if (window.__mostlyagentPreviewSurfaceSync) return;',
  '  window.__mostlyagentPreviewSurfaceSync = true;',
  '  function isTransparent(color) {',
  '    return !color || color === "transparent" || /rgba?\\(\\s*0\\s*,\\s*0\\s*,\\s*0\\s*,\\s*0\\s*\\)/.test(color);',
  '  }',
  '  function firstSurfaceColor(root) {',
  '    var viewportWidth = Math.max(1, window.innerWidth || 1);',
  '    var viewportHeight = Math.max(1, window.innerHeight || 1);',
  '    var nodes = [root.firstElementChild].concat(Array.prototype.slice.call(root.querySelectorAll("main, section, header, [class*=page], [class*=app], [class*=screen], [class*=container]"), 0, 60)).filter(Boolean);',
  '    var bestColor = "";',
  '    var bestArea = 0;',
  '    for (var i = 0; i < nodes.length; i += 1) {',
  '      var el = nodes[i];',
  '      var rect = el.getBoundingClientRect();',
  '      if (rect.width < viewportWidth * 0.55 || rect.height < 24) continue;',
  '      var bg = window.getComputedStyle(el).backgroundColor;',
  '      if (isTransparent(bg)) continue;',
  '      var area = rect.width * Math.min(rect.height, viewportHeight);',
  '      if (area > bestArea) { bestArea = area; bestColor = bg; }',
  '    }',
  '    if (bestColor) return bestColor;',
  '    var bodyBg = window.getComputedStyle(document.body).backgroundColor;',
  '    return isTransparent(bodyBg) ? "#ffffff" : bodyBg;',
  '  }',
  '  function syncSurface() {',
  '    var root = document.getElementById("root");',
  '    if (!root) return;',
  '    var color = firstSurfaceColor(root);',
  '    document.documentElement.style.background = color;',
  '    document.body.style.background = color;',
  '    document.documentElement.style.margin = "0";',
  '    document.body.style.margin = "0";',
  '    document.documentElement.style.width = "100%";',
  '    document.body.style.width = "100%";',
  '    root.style.width = "100%";',
  '    root.style.minHeight = "100vh";',
  '    root.style.backgroundColor = color;',
  '    document.documentElement.style.overflowX = "hidden";',
  '    document.body.style.overflowX = "hidden";',
  '  }',
  '  window.addEventListener("load", function () {',
  '    requestAnimationFrame(syncSurface);',
  '    setTimeout(syncSurface, 250);',
  '    setTimeout(syncSurface, 1000);',
  '  });',
  '})();',
  '</script>',
].join('\n');

function ensurePreviewSurfaceScript(html: string): string {
  if (html.includes('__mostlyagentPreviewSurfaceSync')) return html;
  if (html.includes('</body>')) {
    return html.replace('</body>', `${PREVIEW_SURFACE_SYNC_SCRIPT}\n</body>`);
  }
  return `${html}\n${PREVIEW_SURFACE_SYNC_SCRIPT}`;
}

const THREE_CDN = '<script src="https://unpkg.com/three@latest/build/three.min.js"></script>';
const LUCIDE_CDN = '<script src="https://unpkg.com/lucide@latest/dist/umd/lucide.min.js"></script>';

export function buildIframeHtml(jsxCode: string, pageName: string): string {
  const reactCdn = 'https://unpkg.com/umd-react/dist/react.production.min.js';
  const reactDomCdn = 'https://unpkg.com/umd-react/dist/react-dom.production.min.js';
  const babelCdn = 'https://unpkg.com/@babel/standalone/babel.min.js';
  // Check if Three.js is required
  const needsThree = /\bTHREE\b/.test(jsxCode) || /\bthree\b/.test(jsxCode);
  
  // Strip import statements so standalone Babel doesn't choke.
  const strippedJsx = jsxCode.replace(/^\s*import\s+.*?['"].*?['"]\s*;?\s*$/gm, '');

  return [
    '<!DOCTYPE html><html><head>',
    '<meta charset="UTF-8"/><meta name="viewport" content="width=device-width, initial-scale=1.0"/>',
    `<title>${pageName}</title>`,
    `<script src="${reactCdn}"></script>`,
    `<script src="${reactDomCdn}"></script>`,
    `<script src="${babelCdn}"></script>`,
    needsThree ? THREE_CDN : '',
    '<style>',
    '* { margin: 0; padding: 0; box-sizing: border-box; }',
    "body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }",
    '::-webkit-scrollbar { width: 8px; height: 8px; }',
    '::-webkit-scrollbar-track { background: transparent; }',
    '::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }',
    '::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.22); } ::-webkit-scrollbar-thumb:active { background: rgba(255,255,255,0.32); }</style>',
    '<script>var exports={};var module={exports:exports};var require=function(){return {};};</script>',
    '<script>window.__preKeys = new Set(Object.keys(window));</script>',
    '</head><body><div id="root"></div>',
    `<script type="text/babel">${strippedJsx}`,
    'const root = ReactDOM.createRoot(document.getElementById("root"));',
    'const _userFns = Object.keys(window).filter(k => !window.__preKeys.has(k) && typeof window[k] === "function" && /^[A-Z]/.test(k));',
    'const Component = _userFns.length > 0 ? window[_userFns[_userFns.length - 1]] : () => React.createElement("div", null, "No component");',
	    'root.render(React.createElement(Component));',
	    '</script>',
	    PREVIEW_SURFACE_SYNC_SCRIPT,
	    IFRAME_LINK_INTERCEPT_SCRIPT,
	    '</body></html>',
  ].join('\n');
}

/** Ensure the link interceptor and Lucide CDN are present in compiled HTML. */
export function ensureInterceptScript(html: string): string {
  // Fix Vite singlefile quirk: <style rel="stylesheet" crossorigin> → <style>
  // Browsers ignore CSS in <style> tags with link-only attributes.
  let patched = html.replace(/<style\s+rel="stylesheet"[^>]*>/gi, '<style>');

  // Strip `crossorigin` from inline module scripts — srcdoc iframes have
  // origin "null" and crossorigin on inline scripts is meaningless but can
  // break WebGL context creation.  Keep type="module" for deferred execution.
  patched = patched.replace(/<script\s+type="module"\s+crossorigin\s*>/gi, '<script type="module">');

  // Detect Lucide icons
  const needsLucide = !patched.includes('lucide.min.js') && !patched.includes('lucide@') &&
    (/\blucide\b/.test(patched) ||
     /\bicon:\s*(?:Users|Star|Shield|Lock|Zap|Heart|Check|Mail|Phone|Globe|Search|Menu|Home|Settings|Bell|Calendar|Clock|Eye|Download|Upload|Trash|Edit|Plus|Minus|X|ArrowRight|ArrowLeft|ChevronRight|ChevronDown|ChevronUp|ChevronLeft|ExternalLink|Link|Image|Camera|Mic|Play|Pause|Volume|Wifi|Cloud|Database|Server|Code|Terminal|GitBranch|Layers|Box|Grid|List|Map|Navigation|Compass|Bookmark|Tag|Flag|Award|Crown|Sparkles|Sun|Moon|Cpu|Monitor|Smartphone|Tablet|Watch|Headphones|Speaker|Battery|Bluetooth|Cast|Printer|Save|Copy|Clipboard|File|Folder|FolderKanban|Archive|Package|Gift|ShoppingCart|CreditCard|DollarSign|TrendingUp|BarChart|PieChart|Activity|Target|Crosshair|AlertCircle|AlertTriangle|Info|HelpCircle|MessageCircle|MessageSquare|Send|Share|ThumbsUp|ThumbsDown|UserPlus|UserMinus|UserCheck|Users|Building2|Briefcase|GraduationCap|Book|BookOpen|Pen|Pencil|Paintbrush|Palette|Scissors|Wrench|Hammer|Tool|Key|Unlock|Shield|ShieldCheck|ShieldAlert|Fingerprint|Scan|QrCode|Workflow|RefreshCw|RotateCcw|Repeat|Shuffle|Filter|SortAsc|SortDesc|Maximize|Minimize|Move|Anchor|Aperture|Feather|Flame|Droplet|Wind|Snowflake|Leaf|TreePine|Mountain|Waves|Sunrise|Sunset)\b/.test(patched));
  if (needsLucide) {
    patched = patched.replace('</head>', `${LUCIDE_CDN}\n</head>`);
  }

  if (/\bTHREE\b/.test(patched) && !patched.includes('three.min.js') && !patched.includes('three@')) {
    patched = patched.replace('</head>', `${THREE_CDN}\n</head>`);
  }

  if (patched.includes('design-navigate')) {
    // Replace the old baked-in script from already-generated designs
    // so they get the latest routing features (form submission, etc.)
    const oldScriptRegex = /<script>\s*function __isPreviewNavigableHref[\s\S]*?<\/script>/;
    if (oldScriptRegex.test(patched)) {
      return ensurePreviewSurfaceScript(patched.replace(oldScriptRegex, IFRAME_LINK_INTERCEPT_SCRIPT));
    }
    return ensurePreviewSurfaceScript(patched);
  }

  return ensurePreviewSurfaceScript(patched.replace('</body>', `${IFRAME_LINK_INTERCEPT_SCRIPT}\n</body>`));
}
